import { useCallback, useEffect, useState } from "react";
import useApi from "./useApi";
import {
  loadWorkflowDefinition,
  loadWorkflowRunDefinition,
  WorkflowDefinitionLoadResult,
} from "../services/workflowService";
import { mapWorkflowGraphToDefinition } from "../features/workflows/workflowRunGraphMapper";
import { WorkflowDefinition } from "../features/workflows/workflowDefinitionsData";

export type WorkflowDefinitionPreviewState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "loaded"; definition: WorkflowDefinition }
  | { status: "not-found" }
  | { status: "forbidden" }
  | { status: "error" };

/** What a run starts from: a definition version (Library Run, Builder test — Library / Builder
 *  users), or a workflow's Token, which reads its latest published version without Library access
 *  (how a Hiring Supervisor starts a PIF). */
export type WorkflowRunStartRef = { definitionId: number } | { token: string };

/**
 * Loads a real definition ahead of starting a case for it, so the Run screen can tell whether it
 * is the PIF (which must not start until its HR gate says "Yes") before anything is written.
 * Pass null and the hook stays idle.
 */
const useWorkflowDefinitionPreview = (ref: WorkflowRunStartRef | null) => {
  const { executeApi } = useApi();
  const definitionId = ref && "definitionId" in ref ? ref.definitionId : null;
  const token = ref && "token" in ref ? ref.token : null;
  const [state, setState] = useState<WorkflowDefinitionPreviewState>({
    status: definitionId || token ? "loading" : "idle",
  });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!definitionId && !token) {
      setState({ status: "idle" });
      return undefined;
    }
    let active = true;
    setState({ status: "loading" });
    const load = token
      ? executeApi<string, WorkflowDefinitionLoadResult>(loadWorkflowRunDefinition, token)
      : executeApi<number, WorkflowDefinitionLoadResult>(loadWorkflowDefinition, definitionId as number);
    load.then((result) => {
      if (!active) return;
      if (!result) setState({ status: "error" });
      else if (result.kind === "loaded")
        setState({ status: "loaded", definition: mapWorkflowGraphToDefinition(result.graph) });
      else setState({ status: result.kind });
    });
    return () => {
      active = false;
    };
  }, [executeApi, definitionId, token, attempt]);

  const retry = useCallback(() => setAttempt((current) => current + 1), []);

  return { state, retry };
};

export default useWorkflowDefinitionPreview;
